// Performance Dashboard Types - Sentry Only (Replicating Sentry UI)

export type PeriodFilter = '24h' | '7d' | '30d';
export type MetricStatus = 'good' | 'meh' | 'poor' | 'unknown';
export type DataSource = 'sentry' | 'sentry-partial' | 'not-configured' | 'error';
export type SentryIssueStatusFilter = 'unresolved' | 'resolved' | 'ignored' | 'history' | 'all';

export type WebVitalKey = 'lcp' | 'fcp' | 'cls' | 'fid' | 'inp' | 'ttfb';

export interface WebVitals {
  lcp: number | null;
  fcp: number | null;
  cls: number | null;
  fid: number | null;
  inp: number | null;
  ttfb: number | null;
}

export interface MetricScores {
  lcp: number;
  fcp: number;
  cls: number;
  fid: number;
  inp: number;
  ttfb: number;
}

export interface PerformanceStats {
  errorCount: number;
  transactionCount: number;
  throughput: number;
  unresolvedIssueCount?: number;
  readyToResolveCount?: number;
}

export type SentryOperationalStatus = 'needs_fix' | 'monitoring' | 'ready_to_close' | 'resolved' | 'ignored';

export type SentryIssueActionType = 'resolved' | 'ignored' | 'reopened' | 'marked_monitoring';

export interface SentryIssueActionLog {
  id: string;
  issueId: string;
  action: SentryIssueActionType;
  actorUserId?: string | null;
  actorNameSnapshot?: string | null;
  resultStatus: 'success' | 'failed';
  errorMessage?: string | null;
  createdAt: string;
}

export interface SentryError {
  id: string;
  title: string;
  culprit: string;
  count: number;
  userCount: number;
  firstSeen: string;
  lastSeen: string;
  level: 'error' | 'warning' | 'info';
  status?: string;
  sentryStatus?: string;
  operationalStatus?: SentryOperationalStatus;
  permalink: string;
  type?: string;
  message?: string;
  filename?: string;
  lastActionAt?: string | null;
  lastActionBy?: string | null;
  lastActionResult?: 'success' | 'failed' | null;
  lastActionType?: SentryIssueActionType | null;
  actionHistory?: SentryIssueActionLog[];
}

export interface PageMetric {
  path: string;
  lcp: number | null;
  fcp: number | null;
  cls: number | null;
  ttfb: number | null;
  inp: number | null;
  samples: number;
}

export interface TransactionData {
  transaction: string;
  tpm: number;
  p50: number;
  p75: number;
  p95: number;
  failureRate: number;
  users: number;
  timeSpent: number;
  count: number;
}

export interface TimeSeriesPoint {
  timestamp: string;
  value: number;
}

export interface DurationSeriesPoint {
  timestamp: string;
  p50: number;
  p75: number;
  p95: number;
}

export interface TimeSeries {
  throughput: TimeSeriesPoint[];
  duration: DurationSeriesPoint[];
}

export interface PerformanceFilters {
  period: PeriodFilter;
  issueStatus?: SentryIssueStatusFilter;
}

export interface SentryPerformanceData {
  webVitals: WebVitals;
  metricScores: MetricScores;
  performanceScore: number;
  stats: PerformanceStats;
  errors: SentryError[];
  pages: PageMetric[];
  transactions: TransactionData[];
  timeSeries: TimeSeries;
  dataSource: DataSource;
  fetchedAt?: string;
}

// Web Vitals configuration with Portuguese labels (matching Sentry UI)
export const WEB_VITALS_CONFIG: Record<WebVitalKey, {
  name: string;
  fullName: string;
  fullNameEN: string;
  unit: string;
  thresholds: { good: number; poor: number };
  tooltip: string;
}> = {
  lcp: {
    name: 'LCP',
    fullName: 'Maior Renderização de Conteúdo',
    fullNameEN: 'Largest Contentful Paint',
    unit: 's',
    thresholds: { good: 2500, poor: 4000 },
    tooltip: 'Mede o tempo até o maior elemento visível carregar. Bom: < 2.5s, Médio: 2.5-4s, Ruim: > 4s',
  },
  fcp: {
    name: 'FCP',
    fullName: 'Primeira Renderização de Conteúdo',
    fullNameEN: 'First Contentful Paint',
    unit: 's',
    thresholds: { good: 1800, poor: 3000 },
    tooltip: 'Mede o tempo até o primeiro conteúdo aparecer na tela. Bom: < 1.8s, Médio: 1.8-3s, Ruim: > 3s',
  },
  cls: {
    name: 'CLS',
    fullName: 'Mudança Cumulativa de Layout',
    fullNameEN: 'Cumulative Layout Shift',
    unit: '',
    thresholds: { good: 0.1, poor: 0.25 },
    tooltip: 'Mede a estabilidade visual - quanto a página "pula". Bom: < 0.1, Médio: 0.1-0.25, Ruim: > 0.25',
  },
  fid: {
    name: 'FID',
    fullName: 'Atraso da Primeira Interação',
    fullNameEN: 'First Input Delay',
    unit: 'ms',
    thresholds: { good: 100, poor: 300 },
    tooltip: 'Mede o tempo até a página responder ao primeiro clique. Bom: < 100ms, Médio: 100-300ms, Ruim: > 300ms',
  },
  inp: {
    name: 'INP',
    fullName: 'Interação até Próxima Pintura',
    fullNameEN: 'Interaction to Next Paint',
    unit: 'ms',
    thresholds: { good: 200, poor: 500 },
    tooltip: 'Mede a responsividade geral das interações. Bom: < 200ms, Médio: 200-500ms, Ruim: > 500ms',
  },
  ttfb: {
    name: 'TTFB',
    fullName: 'Tempo até Primeiro Byte',
    fullNameEN: 'Time To First Byte',
    unit: 'ms',
    thresholds: { good: 800, poor: 1800 },
    tooltip: 'Tempo até o servidor enviar o primeiro byte. Bom: < 800ms, Médio: 800-1800ms, Ruim: > 1800ms',
  },
};

// Status configuration matching Sentry UI (Good, Meh, Poor)
export const STATUS_CONFIG: Record<MetricStatus, {
  label: string;
  labelEN: string;
  icon: string;
  color: string;
  bgColor: string;
  textColor: string;
  borderColor: string;
}> = {
  good: {
    label: 'Bom',
    labelEN: 'Good',
    icon: '✅',
    color: 'hsl(142, 76%, 36%)',
    bgColor: 'bg-green-500',
    textColor: 'text-white',
    borderColor: 'border-green-500/30',
  },
  meh: {
    label: 'Médio',
    labelEN: 'Meh',
    icon: '🟡',
    color: 'hsl(38, 92%, 50%)',
    bgColor: 'bg-orange-500',
    textColor: 'text-white',
    borderColor: 'border-orange-500/30',
  },
  poor: {
    label: 'Ruim',
    labelEN: 'Poor',
    icon: '🔴',
    color: 'hsl(0, 84%, 60%)',
    bgColor: 'bg-red-500',
    textColor: 'text-white',
    borderColor: 'border-red-500/30',
  },
  unknown: {
    label: '-',
    labelEN: '-',
    icon: '⚪',
    color: 'hsl(0, 0%, 50%)',
    bgColor: 'bg-muted',
    textColor: 'text-muted-foreground',
    borderColor: 'border-muted',
  },
};

// Donut chart colors for Web Vitals (matching Sentry UI)
export const WEB_VITAL_COLORS: Record<WebVitalKey, string> = {
  lcp: '#6C5FC7', // Purple
  fcp: '#E1567C', // Pink
  cls: '#F5B000', // Yellow
  fid: '#00A4C9', // Cyan
  inp: '#57BE8C', // Green
  ttfb: '#F97316', // Orange
};

// Helper function to get status from score
export function getStatusFromScore(score: number): MetricStatus {
  if (score >= 90) return 'good';
  if (score >= 50) return 'meh';
  return 'poor';
}

// Helper function to get status from metric value
export function getMetricStatus(metricKey: WebVitalKey, value: number | null): MetricStatus {
  if (value === null || value === undefined) return 'unknown';
  
  const config = WEB_VITALS_CONFIG[metricKey];
  if (!config) return 'unknown';
  
  if (value <= config.thresholds.good) return 'good';
  if (value <= config.thresholds.poor) return 'meh';
  return 'poor';
}

// Helper function to format metric value
export function formatMetricValue(metricKey: WebVitalKey, value: number | null): string {
  if (value === null || value === undefined) return '-';
  
  if (metricKey === 'cls') {
    return value.toFixed(2);
  }
  
  if (metricKey === 'lcp' || metricKey === 'fcp') {
    // LCP/FCP come in ms, display in seconds
    return `${(value / 1000).toFixed(2)}s`;
  }
  
  // FID, INP, TTFB - display in ms
  return `${Math.round(value)}ms`;
}

// Helper function to format duration (ms to readable)
export function formatDuration(ms: number): string {
  if (ms < 1000) return `${Math.round(ms)}ms`;
  if (ms < 60000) return `${(ms / 1000).toFixed(2)}s`;
  return `${(ms / 60000).toFixed(1)}m`;
}

// Helper function to format time ago in Portuguese
export function formatTimeAgo(dateString: string): string {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return 'agora';
  if (diffMins < 60) return `há ${diffMins} min`;
  if (diffHours < 24) return `há ${diffHours}h`;
  if (diffDays === 1) return 'ontem';
  return `há ${diffDays} dias`;
}

// Format large numbers
export function formatNumber(num: number): string {
  if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
  if (num >= 1000) return `${(num / 1000).toFixed(1)}K`;
  return num.toFixed(0);
}

// Format percentage
export function formatPercentage(value: number): string {
  return `${(value * 100).toFixed(1)}%`;
}
